// Chat Connection Module - WebSocket 연결 로직
// 메인 chat_controller.js에서 import하여 사용

import consumer from "../../channels/consumer"
import { createLogger } from "../../lib/logger"
import { getI18nMessage } from "../../lib/i18n_helper"

const log = createLogger('ChatConnection')

export const ConnectionMixin = {
  // WebSocket 연결 설정
  setupCable() {
    this.subscription = consumer.subscriptions.create(
      { channel: "ChatChannel", chat_id: this.chatIdValue },
      {
        connected: () => {
          this.isConnected = true
          log.debug('Connected to ChatChannel')
        },

        disconnected: () => {
          this.isConnected = false
          log.debug('Disconnected from ChatChannel')
        },

        received: (data) => {
          log.debug('Message received', data)

          // ⭐ Debug: Log full data structure for troubleshooting
          if (typeof data === 'object' && data !== null) {
            log.debug('Message data keys:', Object.keys(data))
            log.debug('Message content preview:', data.content?.substring?.(0, 50) || '(no content)')
          }

          if (data.error) {
            if (data.needs_verification) {
              this.handleVerificationRequired(data.message)
              return
            }

            this.handleSendFailure(data.message)
            return
          }

          if (this.locationVerificationExpired()) {
            this.handleVerificationRequired(this.locationVerificationRequiredMessage())
            return
          }

          // Handle read receipt broadcasts
          if (data.type === "read_receipt") {
            this.handleReadReceipts(data.message_ids)
            return
          }

          // A retried draft the server had already stored
          if (data.type === "ack") {
            if (data.client_id === this.pendingClientId) this.handleSendSuccess(this.lastDraft)
            return
          }

          // ✅ 자신의 메시지 에코 확인 (전송 성공)
          const ownMessage = data.user_id === this.currentUserIdValue
          if (ownMessage) {
            this.handleSendSuccess(data.content)
          }

          // 메시지 수신 처리
          try {
            this.appendMessage(data)
            this.scrollToLatest()
            log.debug('Message appended successfully, id:', data.id)
          } catch (error) {
            log.error('Failed to append/scroll message:', error)
          }

          // Seen as it arrives: read now, so the badge doesn't climb and the sender sees 已讀
          if (!ownMessage && data.id && document.visibilityState === "visible") {
            this.subscription?.perform("mark_as_read", { message_id: data.id })
          }
        }
      }
    )

    // Messages that arrived while the tab/app was in the background are read when it comes back
    this.markVisibleAsRead = () => {
      if (document.visibilityState === "visible" && this.isConnected) this.subscription?.perform("mark_all_as_read")
    }
    document.addEventListener("visibilitychange", this.markVisibleAsRead)
  },

  teardownCable() {
    if (this.markVisibleAsRead) document.removeEventListener("visibilitychange", this.markVisibleAsRead)
    if (this.subscription) this.subscription.unsubscribe()
  },

  scheduleLocationVerificationExpiry() {
    if (!this.hasLocationVerifiedUntilValue || !this.locationVerifiedUntilValue) return

    const expiresAt = Date.parse(this.locationVerifiedUntilValue)
    if (Number.isNaN(expiresAt)) return

    const delay = expiresAt - Date.now()
    if (delay <= 0) {
      this.handleVerificationRequired(this.locationVerificationRequiredMessage())
      return
    }

    this.locationVerificationTimeout = window.setTimeout(() => {
      this.handleVerificationRequired(this.locationVerificationRequiredMessage())
    }, delay)
  },

  locationVerificationExpired() {
    if (!this.hasLocationVerifiedUntilValue || !this.locationVerifiedUntilValue) return false

    const expiresAt = Date.parse(this.locationVerifiedUntilValue)
    return !Number.isNaN(expiresAt) && Date.now() >= expiresAt
  },

  locationVerificationRequiredMessage() {
    return getI18nMessage(this.element, 'errorsLocationRequired', '傳送訊息需要驗證位置')
  },

  // 입력 이벤트 처리
  handleInput(event) {
    // 텍스트 영역 자동 높이 조절
    const textarea = event.target
    textarea.style.height = "auto"
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px"
  }
}
